/*
 * @Author: your name
 * @Date: 2020-11-24 16:17:32
 * @LastEditTime: 2021-07-29 20:39:11
 * @LastEditors: Please set LastEditors
 * @Description: In User Settings Edit
 * @FilePath: \dhr_frontend\src\utils\axios.js
 */
import Axios from "axios";
import { message } from "ant-design-vue";
import router from "@/routes/index";
import store from "@/store/index";
import { downloadBlob } from "@/utils/helpers";
import {
  ensureTemToken,
  getTemAuthApiPrefix,
  buildTemAuthHeaders,
  clearTemToken,
  isRetryableTemAuthError,
  isTemAuthSuccess
} from "@/utils/tem-auth";

const instance = Axios.create({
  timeout: 60 * 1000,
  withCredentials: true,
});

/**
 * 目前请求支持的额外配置
 *
 * {
 *    type: '' // 请求参数类型, 可选 form
 *    operate: '' // 请求操作类型，可选 downloadFile：自动导出文件
 *    downloadFileName: '', // 自定义下载文件的名
 *    downloadFileType: '', // 下载文件的类型，有时候后端返回的二进制格式不对，需要前端这边强制改为如 pdf，docx等格式
 *    urlParams： {} // 地址拼接参数
 *    uselsmyAccount: false, // 是否在header传入lsmyAccount
 *    errorHandle: (e) => Boolean // 自定义错误处理
 * }
 */
instance.interceptors.request.use(
  (config) => {
    // 参数类型处理
    if (config.type === "form") {
      // form 类型
      config.headers["Content-Type"] =
        "application/x-www-form-urlencoded;charset=UTF-8";

      if (config.data) {
        let formData = new FormData();
        Object.entries(config.data).forEach(([key, value]) => {
          formData.append(key, value);
        });

        config.data = formData;
      }
    }

    // 请求操作处理
    if (config.operate === "downloadFile") {
      // 下载文件，一般用于导出
      config.responseType = "blob";
    }

    // 处理地址拼接参数
    if (config.urlParams) {
      let urlData = new URLSearchParams();

      Object.entries(config.urlParams).forEach(([key, value]) => {
        urlData.append(key, value);
      });

      config.url += `?${urlData.toString()}`;
    }

    // 处理要传入 lsmyAccount 情况
    if (config.uselsmyAccount && process.env.NODE_ENV === "development") {
      config.headers["lsmyAccount"] = store.state?.user?.userInfo?.code || process.env.VUE_APP_LOCAL_ACCOUNT_NO;
    }

    config.headers = Object.assign({}, config.headers || {}, {
      isJsonResponse: "Y",
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    const {
      data,
      config: { operate, downloadFileName, downloadFileType },
    } = response;

    if (operate === "downloadFile") {
      let filename = ""; // 文件名
      let disposition = response.headers["content-disposition"];
      if (downloadFileName) {
        filename = downloadFileName;
      } else if (disposition) {
        let match = disposition.match(/filename=(.*)/);
        if (match?.length) {
          filename = match[1];
        }
      }

      let blobData = data;

      if (downloadFileType) {
        
        blobData = new Blob([data], { type: downloadFileType });
      }
      
      downloadBlob(blobData, decodeURIComponent(filename));
    }

    if (data instanceof Blob) {
      if (data.type !== 'application/json') {
        return Promise.resolve(response);
      } else { // 如果是json格式，就是返回了报错信息

        return data.text().then((str) => {
          let dataObj = JSON.parse(str)

          if (dataObj.code === '-1') {
            message.error(h => h('span', {style: {display: 'inline-block', whiteSpace: 'pre-wrap', verticalAlign: 'top' }}, dataObj.msg));

            return Promise.reject(dataObj);
          } else {
            Promise.resolve(response)
          }
        })
      }
    }

    if (data) {
      if (data.code == "302") {
        window.location.href = `${data.content}`;
        return;
      }
      if (data.code == "1") {
        return Promise.resolve(data);
      } else if (response.config.errorHandle) {
        let flag = response.config.errorHandle(data);

        if (flag) {
          return Promise.resolve(data);
        } else {
          return Promise.reject(data);
        }
      } else {
        message.error(h => h('span', {style: {display: 'inline-block', whiteSpace: 'pre-wrap', verticalAlign: 'top' }}, data.msg));
        if (data.code === 401) {
          router.push("/login").then();
        }
        if (data.code === 403) {
          router.push("/404").then();
        }
        console.warn(`${response.config.url}出现错误`);
        return Promise.reject(data);
      }
    }
    console.warn(`${response.config.url}出现错误`);
    return Promise.reject("接口错误");
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.temGet = function(url = "", params = {}, config = {}) {
  return requestTemAuth("get", url, params, config);
};

instance.temPost = function(url = "", data = {}, config = {}) {
  return requestTemAuth("post", url, data, config);
};

async function requestTemAuth(method, url, data, config) {
  const token = await ensureTemToken();

  try {
    return await sendTemRequest(method, url, data, config, token);
  } catch (error) {
    if (isRetryableTemAuthError(error)) {
      if (process.env.NODE_ENV === "development") {
        console.log("[tem-auth] retry with new token", error);
      }
      clearTemToken();
      const newToken = await ensureTemToken();
      return sendTemRequest(method, url, data, config, newToken);
    }

    showTemAuthError(error);
    throw error;
  }
}

async function sendTemRequest(method, url, data, config, token) {
  const response = await instance({
    ...config,
    method,
    url: buildTemUrl(url),
    [method === "get" ? "params" : "data"]: data,
    headers: {
      ...(config.headers || {}),
      ...buildTemAuthHeaders(token),
    },
    errorHandle() {
      return true;
    },
  });

  if (isTemAuthSuccess(response)) {
    return response;
  }

  throw response;
}

function buildTemUrl(url) {
  const apiPrefixMatch = url.match(/^(\/[^/]+)(.*)$/);
  if (apiPrefixMatch) {
    return apiPrefixMatch[1] + getTemAuthApiPrefix() + apiPrefixMatch[2];
  }
  return getTemAuthApiPrefix() + url;
}

function showTemAuthError(error) {
  const msg = error && (error.msg || error.info || error.message);
  if (msg) {
    message.error(h => h("span", { style: { display: "inline-block", whiteSpace: "pre-wrap", verticalAlign: "top" } }, msg));
  }
}

export default instance;
